import React, { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/config/endpoints";
import { authService } from "@/services/auth.service";
import { Button } from "@/components/ui/button";
import {
  MoreHorizontal,
  Trash2,
  Check,
  X,
  Pencil,
  History,
  Download,
  Printer,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import EditRequestStatusModal from "@/components/EditRequestStatusModal";
import {
  generateApprovalHistoryPDF,
  printApprovalHistory,
  generateDeliveryImagesPDF,
  printDeliveryImages,
} from "@/utils/pdfUtils";

interface ApprovalHistory {
  approver: string;
  status: "approved" | "rejected";
  comments: string;
  _id: string;
  timestamp: string;
}

interface Site {
  _id: string;
  title: string;
  location: string;
  siteType: string;
  siteManager: {
    id: string;
    name: string;
    phone: string;
  };
}

interface SupplierAllocation {
  _id: string;
  supplierId: string;
  supplierName: string;
  supplierEmail: string;
  supplierPhone: string;
  quantity: string;
  supplierStatus: string;
  timestamp: string;
}

interface Request {
  _id: string;
  orderId: string;
  requestId: string;
  itemName: string;
  qualityType: string;
  productType: string;
  quantity: number | string;
  location: string;
  pricePerUnit: number;
  estimatedPrice: number;
  role: string;
  siteId: string;
  phase: string;
  type: string;
  status:
    | "pending"
    | "in_progress"
    | "completed"
    | "rejected"
    | "delivered"
    | "settled"
    | "canceled";
  currentApprover: string;
  createdBy: {
    _id: string;
    name: string;
    email: string;
    phone: string;
    role: string;
  };
  approvalHistory: ApprovalHistory[];
  supplierAllocations?: SupplierAllocation[];
  createdAt: string;
  updatedAt: string;
  deliveryImages?: Array<{
    url: string;
    timestamp: string;
    uploadedAt: string;
    _id: string;
  }>;
}

const Requests = () => {
  const queryClient = useQueryClient();
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);
  const [showHistory, setShowHistory] = useState<Request | null>(null);
  const [showEditStatusModal, setShowEditStatusModal] =
    useState<Request | null>(null);
  const [showSupplierModal, setShowSupplierModal] = useState<Request | null>(
    null
  );
  const [expandedImageIndex, setExpandedImageIndex] = useState<number | null>(
    null
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRequests, setSelectedRequests] = useState<string[]>([]);
  const itemsPerPage = 10;

  const [siteMap, setSiteMap] = useState<Record<string, Site>>({});

  const { data: sites } = useQuery({
    queryKey: ["sites"],
    queryFn: async () => {
      try {
        const { token } = authService.getStoredAuth();
        if (!token) {
          throw new Error("No authentication token found");
        }

        const response = await fetch(endpoints.sites.list, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            try {
              const newToken = await authService.refreshToken();
              const retryResponse = await fetch(endpoints.sites.list, {
                headers: {
                  Authorization: `Bearer ${newToken}`,
                },
              });
              if (!retryResponse.ok) {
                throw new Error("Failed to fetch sites after token refresh");
              }
              const sitesData = await retryResponse.json();
              processSitesData(sitesData);
              return sitesData;
            } catch (error) {
              authService.logout();
              throw new Error("Session expired. Please login again.");
            }
          }
          throw new Error("Failed to fetch sites");
        }

        const sitesData = await response.json();
        processSitesData(sitesData);
        return sitesData;
      } catch (error) {
        console.error("Error fetching sites:", error);
        return [];
      }
    },
  });

  const processSitesData = (sitesData: Site[]) => {
    const mapping: Record<string, Site> = {};
    sitesData.forEach((site) => {
      mapping[site._id] = site;
    });
    setSiteMap(mapping);
  };

  const {
    data: requests,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["requests"],
    queryFn: async () => {
      const { token } = authService.getStoredAuth();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await fetch(endpoints.requests.list, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          try {
            const newToken = await authService.refreshToken();
            const retryResponse = await fetch(endpoints.requests.list, {
              headers: {
                Authorization: `Bearer ${newToken}`,
              },
            });
            if (!retryResponse.ok) {
              throw new Error("Failed to fetch requests after token refresh");
            }
            return retryResponse.json();
          } catch (error) {
            authService.logout();
            throw new Error("Session expired. Please login again.");
          }
        }
        throw new Error("Failed to fetch requests");
      }
      return response.json();
    },
    refetchInterval: 10000,
  });

  const deleteRequestMutation = useMutation({
    mutationFn: async (requestIds: string | string[]) => {
      const { token } = authService.getStoredAuth();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const isBulkDelete = Array.isArray(requestIds);
      const endpoint = isBulkDelete
        ? endpoints.requests.bulkDelete
        : endpoints.requests.delete(requestIds as string);

      const response = await fetch(endpoint, {
        method: isBulkDelete ? "POST" : "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        ...(isBulkDelete && { body: JSON.stringify({ requestIds }) }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          try {
            const newToken = await authService.refreshToken();
            const retryResponse = await fetch(endpoint, {
              method: isBulkDelete ? "POST" : "DELETE",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${newToken}`,
              },
              ...(isBulkDelete && { body: JSON.stringify({ requestIds }) }),
            });
            if (!retryResponse.ok) {
              throw new Error(
                "Failed to delete request(s) after token refresh"
              );
            }
            return retryResponse.json();
          } catch (error) {
            authService.logout();
            throw new Error("Session expired. Please login again.");
          }
        }
        throw new Error("Failed to delete request(s)");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["requests"] });
      setSelectedRequests([]);
      toast.success("Request(s) deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete request(s)");
    },
  });

  const updateRequestStatusMutation = useMutation({
    mutationFn: async ({
      requestId,
      status,
    }: {
      requestId: string;
      status: Request["status"];
    }) => {
      const { token } = authService.getStoredAuth();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await fetch(endpoints.requests.update(requestId), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          try {
            const newToken = await authService.refreshToken();
            const retryResponse = await fetch(
              endpoints.requests.update(requestId),
              {
                method: "PATCH",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${newToken}`,
                },
                body: JSON.stringify({ status }),
              }
            );
            if (!retryResponse.ok) {
              throw new Error("Failed to update request after token refresh");
            }
            return retryResponse.json();
          } catch (error) {
            authService.logout();
            throw new Error("Session expired. Please login again.");
          }
        }
        throw new Error("Failed to update request");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["requests"] });
      toast.success("Request status updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update request");
    },
  });

  const getStatusBadgeVariant = (status: Request["status"]) => {
    switch (status) {
      case "pending":
        return "warning";
      case "in_progress":
        return "default";
      case "completed":
        return "info";
      case "rejected":
        return "destructive";
      case "delivered":
        return "info";
      case "settled":
        return "info";
      case "canceled":
        return "destructive";
      default:
        return "secondary";
    }
  };

  const getApproverBadgeVariant = (status: "approved" | "rejected") => {
    switch (status) {
      case "approved":
        return "default";
      case "rejected":
        return "destructive";
      default:
        return "secondary";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const handleDelete = (requestId: string) => {
    deleteRequestMutation.mutate(requestId);
  };

  const handleBulkDelete = () => {
    if (selectedRequests.length === 0) {
      toast.error("Please select requests to delete");
      return;
    }
    deleteRequestMutation.mutate(selectedRequests);
  };

  const handleSelectRequest = (requestId: string) => {
    setSelectedRequests((prev) =>
      prev.includes(requestId)
        ? prev.filter((id) => id !== requestId)
        : [...prev, requestId]
    );
  };

  const handleSelectAll = () => {
    if (selectedRequests.length === currentRequests?.length) {
      setSelectedRequests([]);
    } else {
      setSelectedRequests(currentRequests?.map((request) => request._id) || []);
    }
  };

  const handleStatusUpdate = (requestId: string, status: Request["status"]) => {
    updateRequestStatusMutation.mutate({ requestId, status });
  };

  const filteredRequests = requests?.filter((request: Request) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      request.orderId.toLowerCase().includes(searchLower) ||
      request.itemName.toLowerCase().includes(searchLower) ||
      (request.productType?.toLowerCase() || "").includes(searchLower) ||
      (request.phase?.toLowerCase() || "").includes(searchLower) ||
      request.location.toLowerCase().includes(searchLower) ||
      request.status.toLowerCase().includes(searchLower) ||
      request.currentApprover.toLowerCase().includes(searchLower) ||
      (request.createdBy?.name?.toLowerCase() || "").includes(searchLower) ||
      (request.createdBy?.email?.toLowerCase() || "").includes(searchLower) ||
      (request.role?.toLowerCase() || "").includes(searchLower)
    );
  });

  const totalPages = Math.ceil((filteredRequests?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRequests = filteredRequests?.slice(startIndex, endIndex);
  console.log("currentRequests,", currentRequests);

  const handlePrintApprovalHistory = (request: Request) => {
    printApprovalHistory(
      request as unknown as Parameters<typeof printApprovalHistory>[0]
    );
  };

  const handleGenerateApprovalHistoryPDF = async (request: Request) => {
    try {
      await generateApprovalHistoryPDF(
        request as unknown as Parameters<typeof generateApprovalHistoryPDF>[0]
      );
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF. Please try again.");
    }
  };

  const handlePrintDeliveryImages = (request: Request) => {
    printDeliveryImages(
      request as unknown as Parameters<typeof printDeliveryImages>[0]
    );
  };

  const handleGenerateDeliveryImagesPDF = (request: Request) => {
    generateDeliveryImagesPDF(
      request as unknown as Parameters<typeof generateDeliveryImagesPDF>[0]
    );
  };

  const getSiteName = (siteId: string) => {
    if (!siteId) return "Unknown Site";
    const site = siteMap[siteId];
    return site ? site.title : "Unknown Site";
  };

  if (isLoading) {
    return (
      <DashboardLayout title="Requests">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout title="Requests">
        <Card className="w-full">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="rounded-full bg-gray-100 p-4 mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-gray-400"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No Data Found
            </h3>
            <p className="text-sm text-gray-500 text-center max-w-sm">
              There are currently no requests available. Please check back later
              or try refreshing the page.
            </p>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>All Requests</CardTitle>
          <div className="flex items-center gap-4">
            {selectedRequests.length > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Selected ({selectedRequests.length})
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete
                      the selected requests.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleBulkDelete}
                      className="bg-red-500 hover:bg-red-600"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <div className="relative">
              <Input
                placeholder="Search requests..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-[300px]"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">
                  <Checkbox
                    checked={
                      selectedRequests.length === currentRequests?.length
                    }
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
                <TableHead>PO Number</TableHead>
                <TableHead>Item</TableHead>
                <TableHead>Product Type</TableHead>
                <TableHead>Phase</TableHead>
                <TableHead>Quantity</TableHead>
                <TableHead>Site</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Current Stage</TableHead>
                <TableHead>Created By</TableHead>
                <TableHead>Created At</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentRequests?.map((request: Request) => (
                <TableRow key={request._id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedRequests.includes(request._id)}
                      onCheckedChange={() => handleSelectRequest(request._id)}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{request.orderId}</div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{request.itemName}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{request.productType}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{request.phase}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div>{request.quantity}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">
                      {getSiteName(request.siteId)}
                    </div>
                  </TableCell>
                  <TableCell>{request.location}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={
                        request.status === "pending"
                          ? "bg-orange-500 text-white border-orange-500"
                          : request.status === "delivered"
                          ? "bg-blue-500 text-white border-blue-500"
                          : request.status === "settled"
                          ? "bg-green-500 text-white border-green-500"
                          : request.status === "canceled"
                          ? "bg-red-500 text-white border-red-500"
                          : ""
                      }
                    >
                      {request.status === "settled"
                        ? "Received"
                        : request.status.replace("_", " ")}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {request.currentApprover.replace("_", " ")}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div>{request.createdBy?.name || "Unknown User"}</div>
                      <div className="text-sm text-gray-500">
                        {request.createdBy?.phone || "No Number"}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{formatDate(request.createdAt)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-2"
                            onClick={() => setShowHistory(request)}
                          >
                            Approval History
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-h-[80vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>Approval History</DialogTitle>
                          </DialogHeader>
                          <div className="flex justify-end gap-2 mb-3">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-2"
                              onClick={() =>
                                handlePrintApprovalHistory(request)
                              }
                            >
                              <Printer className="h-4 w-4" />
                              Print
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-2"
                              onClick={() =>
                                handleGenerateApprovalHistoryPDF(request)
                              }
                            >
                              <Download className="h-4 w-4" />
                              Download PDF
                            </Button>
                          </div>
                          <div className="space-y-4">
                            {request.approvalHistory.map((history) => (
                              <div
                                key={history._id}
                                className="border rounded-lg p-4"
                              >
                                <div className="flex justify-between items-center">
                                  <Badge
                                    variant={getApproverBadgeVariant(
                                      history.status
                                    )}
                                  >
                                    {history.status}
                                  </Badge>
                                  <span className="text-sm text-gray-500">
                                    {formatDate(history.timestamp)}
                                  </span>
                                </div>
                                <div className="mt-2">
                                  <div className="font-medium">
                                    {history.approver.replace("_", " ")}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    {history.comments}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          {/* Delivery Images Section */}
                          {request.status === "delivered" &&
                            request.deliveryImages &&
                            request.deliveryImages.length > 0 && (
                              <div className="mt-6">
                                <div className="flex justify-between items-center mb-4">
                                  <h3 className="text-lg font-semibold">
                                    Delivery Images
                                  </h3>
                                  <div className="flex gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="flex items-center gap-2"
                                      onClick={() =>
                                        handlePrintDeliveryImages(request)
                                      }
                                    >
                                      <Printer className="h-4 w-4" />
                                      Print
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="flex items-center gap-2"
                                      onClick={() =>
                                        handleGenerateDeliveryImagesPDF(request)
                                      }
                                    >
                                      <Download className="h-4 w-4" />
                                      Download PDF
                                    </Button>
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                  {request.deliveryImages.map(
                                    (image, index) => {
                                      const isExpanded =
                                        expandedImageIndex === index;
                                      return (
                                        <div key={index} className="relative">
                                          <div
                                            className={`cursor-pointer transition-all duration-300 ${
                                              isExpanded
                                                ? "fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4"
                                                : "relative aspect-square"
                                            }`}
                                            onClick={() =>
                                              setExpandedImageIndex(
                                                isExpanded ? null : index
                                              )
                                            }
                                          >
                                            <img
                                              src={image.url}
                                              alt={`Delivery image ${
                                                index + 1
                                              } for PO ${request.orderId}`}
                                              className={`${
                                                isExpanded
                                                  ? "max-h-[90vh] max-w-[90vw] object-contain"
                                                  : "w-full h-full object-cover"
                                              } rounded-lg hover:opacity-90 transition-opacity`}
                                            />
                                            {isExpanded && (
                                              <button
                                                className="absolute top-4 right-4 bg-white rounded-full p-2 text-black hover:bg-gray-100 transition-colors"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setExpandedImageIndex(null);
                                                }}
                                              >
                                                <X className="h-6 w-6" />
                                              </button>
                                            )}
                                          </div>
                                          {!isExpanded && (
                                            <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white p-2 text-sm rounded-b-lg">
                                              <div>PO: {request.orderId}</div>
                                              <div>
                                                Uploaded:{" "}
                                                {formatDate(image.uploadedAt)}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    }
                                  )}
                                </div>
                              </div>
                            )}
                        </DialogContent>
                      </Dialog>

                      {request.supplierAllocations &&
                        request.supplierAllocations.length > 0 && (
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-2"
                                onClick={() => setShowSupplierModal(request)}
                              >
                                View Suppliers
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-h-[80vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle>Supplier Details</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4 mt-4">
                                {request.supplierAllocations.map((supplier) => (
                                  <div
                                    key={supplier._id}
                                    className="border rounded-lg p-4"
                                  >
                                    <div className="flex justify-between items-center">
                                      <div className="font-medium text-lg">
                                        {supplier.supplierName}
                                      </div>
                                      <Badge
                                        variant={
                                          supplier.supplierStatus === "active"
                                            ? "default"
                                            : "secondary"
                                        }
                                      >
                                        {supplier.supplierStatus}
                                      </Badge>
                                    </div>
                                    <div className="mt-2 space-y-1">
                                      <div className="text-sm">
                                        <span className="font-medium">
                                          Email:
                                        </span>{" "}
                                        {supplier.supplierEmail}
                                      </div>
                                      <div className="text-sm">
                                        <span className="font-medium">
                                          Phone:
                                        </span>{" "}
                                        {supplier.supplierPhone}
                                      </div>
                                      <div className="text-sm">
                                        <span className="font-medium">
                                          Quantity:
                                        </span>{" "}
                                        {supplier.quantity}
                                      </div>
                                      <div className="text-sm">
                                        <span className="font-medium">
                                          Allocated on:
                                        </span>{" "}
                                        {formatDate(supplier.timestamp)}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </DialogContent>
                          </Dialog>
                        )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => setShowEditStatusModal(request)}
                          >
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit Status
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => handleDelete(request._id)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-muted-foreground">
              Showing {startIndex + 1} to{" "}
              {Math.min(endIndex, filteredRequests?.length || 0)} of{" "}
              {filteredRequests?.length || 0} requests
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {showEditStatusModal && (
        <EditRequestStatusModal
          isOpen={!!showEditStatusModal}
          onClose={() => setShowEditStatusModal(null)}
          request={
            showEditStatusModal as unknown as Parameters<
              typeof EditRequestStatusModal
            >[0]["request"]
          }
          onStatusUpdate={handleStatusUpdate}
        />
      )}
    </DashboardLayout>
  );
};

export default Requests;
